import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { municipalities } from "@/data/municipalities";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { comparisonOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  operator: comparisonOperators.enum,
  values: z.array(z.coerce.number<string | number>()).min(1, {
    message: "Du skal vælge mindst en kommune",
  }),
});

export function MunicipalitiesSegment({
  queryFilters,
  update,
  formId,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const municipalityCodeFilter = queryFilters.value;

  const { control, handleSubmit, watch, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: municipalityCodeFilter
      ? schema.parse(municipalityCodeFilter)
      : {
          operator: "Equal",
          values: [],
        },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const { values, operator } = watch();

  const submit = handleSubmit((data) => {
    update({
      value: { ...data, values: data.values.map(String) },
    });
  });

  return (
    <div className="flex flex-col gap-y-9">
      <SegmentHeader
        title="Kommune"
        description="Vælg en eller flere kommuner."
      />
      <Form
        onSubmit={submit}
        className="flex size-full h-full flex-col items-center justify-between gap-y-12"
        id={formId}
      >
        <div className="flex flex-col gap-y-6">
          <div className="mx-auto flex gap-x-4">
            <Controller
              control={control}
              name="operator"
              render={({ field, fieldState: { error } }) => (
                <Select
                  placeholder="Operator"
                  aria-label="Operator"
                  selectedKey={field.value}
                  onSelectionChange={field.onChange}
                  items={Object.entries(comparisonOperators.values).map(
                    ([key, value]) => ({
                      id: key,
                      label: value,
                    }),
                  )}
                  errorMessage={error?.message}
                >
                  {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
              )}
            />
            <Controller
              control={control}
              name="values"
              render={({ field, fieldState: { error } }) => (
                <TagField
                  placeholder="Kommune"
                  aria-label="Kommune"
                  selectedOptions={
                    field.value.map((value) => ({
                      label: municipalities.find((m) => m.value === value)!
                        .title,
                      value,
                    })) || []
                  }
                  setSelectedOptions={(value) => {
                    if (Array.isArray(value))
                      return field.onChange(value.map((item) => item.value));
                  }}
                  options={municipalities.map((municipality) => ({
                    label: municipality.title,
                    value: municipality.value,
                  }))}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <ul className="flex flex-col gap-y-2">
            {values.map((value, index) => (
              <ListItem
                key={value}
                label={`${operator === "Equal" ? "Lig med" : "Ikke lig med"} ${
                  municipalities.find((m) => m.value === value)?.title
                }`}
                onRemove={() => {
                  const newValues = [...values];
                  newValues.splice(index, 1);
                  setValue("values", newValues);
                }}
              />
            ))}
          </ul>
        </div>
      </Form>
    </div>
  );
}
